import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package, Truck, AlertCircle } from 'lucide-react';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name, 'executive');
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split">
      {/* Left side - Hero image */}
      <div 
        className="hidden lg:flex relative bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg)' }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-10 h-10 text-[#FF5F00]" strokeWidth={2.5} />
              <span className="font-mono text-2xl font-bold tracking-tight">SND PARCEL</span>
            </div>
            <p className="text-slate-400 text-sm uppercase tracking-wider">Courier Tracking System</p>
          </div>
          
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="font-mono text-4xl font-bold tracking-tight uppercase">
                Track Every<br />Parcel.<br />Every Time.
              </h1>
              <p className="text-slate-300 max-w-md">
                Streamline your incoming and outgoing courier operations with real-time tracking, 
                barcode scanning, and comprehensive reporting.
              </p>
            </div>
            
            <div className="flex gap-8">
              <div>
                <div className="text-3xl font-mono font-bold text-[#FF5F00]">3-Level</div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Dispatch Tracking</div>
              </div>
              <div>
                <div className="text-3xl font-mono font-bold text-[#FF5F00]">Real-time</div>
                <div className="text-xs uppercase tracking-wider text-slate-400">SKU Matching</div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-slate-400">
            <Truck className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-sm">Trusted by logistics teams worldwide</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-slate-50">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Package className="w-8 h-8 text-[#FF5F00]" strokeWidth={2.5} />
            <span className="font-mono text-xl font-bold tracking-tight">SND PARCEL</span>
          </div>

          <div className="mb-8">
            <h2 className="font-mono text-3xl font-bold tracking-tight uppercase text-slate-900">
              {isLogin ? 'Sign In' : 'Create Account'}
            </h2>
            <p className="text-slate-500 mt-2">
              {isLogin 
                ? 'Enter your credentials to access the system' 
                : 'Register to start tracking parcels'}
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border-2 border-red-200 text-red-700" data-testid="login-error">
              <AlertCircle className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-bold tracking-wide uppercase text-slate-600">
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-industrial"
                  placeholder="John Doe"
                  required={!isLogin}
                  data-testid="register-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold tracking-wide uppercase text-slate-600">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-industrial"
                placeholder="you@company.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold tracking-wide uppercase text-slate-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-industrial"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner w-5 h-5" />
                  {isLogin ? 'Signing In...' : 'Creating Account...'}
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-sm font-medium text-slate-600 hover:text-[#FF5F00]"
              data-testid="toggle-auth-mode"
            >
              {isLogin 
                ? "Don't have an account? Create one" 
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
